<template>
  <BaseDrawer
    no-padding
    height-auto-only
    no-toolbar
    no-bottom-rounding
    class="scrollbar-hide"
    close-label="settings"
    @close="$emit('close')"
  >
    <template #content>
      <div class="w-full flex flex-col" style="height: calc(100vh - 80px)">
        <!-- Header -->
        <div class="w-full flex items-center justify-between p-4 border-b border-border-normal">
          <button class="flex items-center gap-1.5 pl-1.5 pr-2.5 py-1.5 rounded-lg transition-colors duration-100 hover:bg-neutral-10"
          @click="$emit('close')">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M11.3214 13.3333L8.57733 10.5893C8.25189 10.2638 8.25189 9.73618 8.57733 9.41074L11.3214 6.66666" stroke="#5E6678" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <BaseText type="label" size="sm" class="text-text-muted">
              Back to all bounties
            </BaseText>
          </button>
          <div class="flex items-center gap-3">
            <button class="flex items-center gap-1.5 pl-1.5 py-1.5 pr-3 rounded-lg transition-colors duration-100 hover:bg-neutral-10">
              <ShareIcon class="text-icon-normal" />
              <BaseText type="label" size="sm" class="text-text-muted">
                Share
              </BaseText>
            </button>
          </div>
        </div>
        <!-- Content -->
        <div class="w-full flex-grow flex flex-col items-center min-h-0 overflow-y-auto">
          <div class="w-full h-full flex flex-col mt-14" style="max-width: 864px;">
            <!-- Bounty Top-Level Info -->
            <BaseText type="label" size="md" class="text-text-muted">
              {{ bounty.title }}
            </BaseText>
            <div class="bounty-info-grid mt-5">
              <BaseText type="body" size="sm" class="text-text-normal">
                {{ bounty.description }}
              </BaseText>
              <div class="w-full h-full flex justify-center">
                <div class="w-0 h-full border-l border-border-normal" />
              </div>
              <div class="bounty-details">
                <!-- Bounty Payout -->
                <BaseText type="body" size="sm" class="text-text-normal">
                  Bounty amount
                </BaseText>
                <div class="flex items-center gap-0.5 pl-1 py-1 pr-1.5 rounded-md w-max" style="background-color: #FFBE4C14; color: #BB8400;">
                  <BountyPayoutGradientIcon />
                  <BaseText type="label" size="sm">
                    {{ bounty.payout.toLocaleString() }}
                  </BaseText>
                </div>
                <!-- Creator Info -->
                <BaseText type="body" size="sm" class="text-text-normal">
                  Published by
                </BaseText>
                <div class="flex items-center gap-2">
                  <img :src="bounty.creator.avatar" class="w-5 h-5 rounded-full" />
                  <BaseText type="label" size="sm" class="text-text-muted">
                    {{ bounty.creator.name }}
                  </BaseText>
                  <BaseText type="body" size="sm" class="text-text-normal">
                    {{ bounty.creator.handle }}
                  </BaseText>
                </div>
                <!-- Due Date -->
                <BaseText type="body" size="sm" class="text-text-normal">
                  Due date
                </BaseText>
                <div class="flex items-center gap-1">
                  <DueDateIcon class="text-icon-normal" />
                  <BaseText type="label" size="sm" class="text-text-normal">
                    Due {{ formattedDueDate }}
                  </BaseText>
                </div>
                <!-- Status -->
                <BaseText type="body" size="sm" class="text-text-normal">
                  Status
                </BaseText>
                <div class="flex items-center gap-2 pl-2 pr-2 py-0.5">
                  <div class="w-1.5 h-1.5 rounded-full bg-secondary-green-200" />
                  <BaseText type="label" size="sm" class="text-secondary-green-200">
                    Open
                  </BaseText>
                </div>
              </div>
            </div>
            <!-- Submit Button -->
            <a class="submit-btn w-full px-3 py-1.5 rounded-md mt-5 text-center"
            href="https://www.foreplay.co/bounties/build-share-a-workflow-using-the-foreplay-api#submit-form" target="_blank">
              <BaseText type="label" size="sm" class="text-white">
                Submit
              </BaseText>
            </a>
            <!-- Tab Selectors -->
            <div class="w-full flex items-center gap-2 py-2 border-b border-border-normal mt-5">
              <div class="relative">
                <button class="tab-selector" :class="{'active': activeTab === 'details'}"
                @click="activeTab = 'details'">
                  <DetailsIcon />
                  <BaseText type="label" size="sm">
                    Details
                  </BaseText>
                </button>
                <transition name="tab">
                  <div v-if="activeTab === 'details'" class="tab-underline" />
                </transition>
              </div>
              <!-- TODO - Re-add when we display bounty submissions -->
              <!-- <div class="relative">
                <button class="tab-selector" :class="{'active': activeTab === 'submissions'}"
                @click="activeTab = 'submissions'">
                  <SubmissionIcon />
                  <BaseText type="label" size="sm">
                    Submissions
                  </BaseText>
                </button>
                <transition name="tab">
                  <div v-if="activeTab === 'submissions'" class="tab-underline" />
                </transition>
              </div> -->
            </div>
            <!-- DETAILS VIEW (TODO - Render from a saved markdown) -->
            <!-- <div v-if="activeTab === 'details'" class="w-full flex flex-col mt-6 fade-in pb-16"> -->
            <div class="w-full flex flex-col mt-6 fade-in pb-16">
              <!-- Youtube Embed -->
              <iframe
                width="100%"
                height="500"
                class="rounded-md"
                src="https://www.youtube.com/embed/TqzUKpgjbwg"
                title="YouTube video player"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen
              />
              <BaseText type="body" size="sm" class="text-text-normal mt-6">
                Creative strategy is evolving fast. The best teams in the world aren’t just browsing ad libraries and writing briefs. They’re building systems. They’re scaling research, iteration, and production, with AI.
                <br/><br/>Now it’s your turn.
                <br/><br/>We’re launching the <span class="font-medium">Foreplay API</span>—and we want to see what you can do with it. To make things interesting, we’ve put <span class="font-medium">$5,000 on the line</span> for whoever builds and shares the most mind-blowing workflow <a class="desc-link" href="https://www.foreplay.co/api" target="_blank">using our API</a>.
              </BaseText>
              <BaseText type="label" size="sm" class="text-text-muted mt-4">
                How to Submit
              </BaseText>
              <BaseText type="body" size="sm" class="text-text-normal mt-2 ml-2">
                <ol class="decimal-list space-y-1">
                  <li>Create a AI Workflow using the Foreplay AP</li>
                  <li>Share it on Social Media and Tag <span class="font-medium">@foreplay_co</span> where applicable</li>
                  <li>Submit the Form on this Page</li>
                </ol>
              </BaseText>
              <BaseText type="label" size="sm" class="text-text-muted mt-4">
                How the Winner will be Selected
              </BaseText>
              <BaseText type="body" size="sm" class="text-text-normal mt-2 ml-2">
                <ol class="decimal-list space-y-1">
                  <li>Finalists will be ranked by social engagement & post popularity</li>
                  <li>Winner will be chosen by Foreplay employees based on usefulness, complexity and public interest</li>
                </ol>
              </BaseText>
              <BaseText type="label" size="sm" class="text-text-muted mt-8">
                Useful Links
              </BaseText>
              <BaseText type="body" size="sm" class="text-text-normal mt-2 ml-2">
                <ul class="space-y-1">
                  <li><a class="desc-link" href="https://chatgpt.com/g/g-68b06c9529548191b5d25556e66a272a-foreplay-api-docs" target="_blank">Foreplay API Doc GPT</a></li>
                  <li><a class="desc-link" href="https://public.api.foreplay.co/docs" target="_blank">Technical Docs</a></li>
                  <li><a class="desc-link" href="https://help.foreplay.co/articles/0374062-getting-started-with-the-foreplay-api" target="_blank">API Getting Started</a></li>
                  <li>Foreplay Socials - <a class="desc-link" href="https://www.instagram.com/foreplay_co/" target="_blank">Instagram</a>, <a class="desc-link" href="https://www.linkedin.com/company/foreplayco/" target="_blank">LinkedIn</a>, <a class="desc-link" href="https://www.youtube.com/channel/UCzLrvXeeuoUW5h4a1kz34cQ" target="_blank">YouTube</a>, <a class="desc-link" href="https://www.tiktok.com/@foreplay_co" target="_blank">TikTok</a>, <a class="desc-link" href="https://x.com/foreplay_co" target="_blank">Twitter/X</a></li>
                </ul>
              </BaseText>
              <BaseText type="label" size="sm" class="text-text-muted mt-4">
                Useful Tools
              </BaseText>
              <BaseText type="body" size="sm" class="text-text-normal mt-2 ml-2">
                <ul class="space-y-1">
                  <li><a class="desc-link" href="https://www.gumloop.com/" target="_blank">Gumloop</a></li>
                  <li><a class="desc-link" href="https://n8n.io/" target="_blank">N8N</a></li>
                  <li><a class="desc-link" href="https://zapier.com/" target="_blank">Zapier</a></li>
                  <li><a class="desc-link" href="https://www.make.com/" target="_blank">Make</a></li>
                </ul>
              </BaseText>
            </div>
            <!-- SUBMISSIONS VIEW -->
            <!-- <div v-if="activeTab === 'submissions'" class="w-full flex flex-col gap-4 mt-5 pb-10 fade-in">
              <BountySubmissionCard 
                v-for="(submission, index) in submissions" 
                :key="`submission-${index}`" 
                :submission="submission" 
              />
            </div> -->
          </div>
        </div>
      </div>
    </template>
  </BaseDrawer>
</template>

<script>
import moment from 'moment'

import BountySubmissionCard from './BountySubmissionCard.vue'

import DueDateIcon from '../globals/Icons/DueDateIcon.vue'
import ShareIcon from '../globals/Icons/ExternalShareIcon.vue'
import BountyPayoutGradientIcon from '../globals/Icons/BountyPayoutGradientIcon.vue'
import DetailsIcon from '../globals/Icons/DetailsIcon.vue'
import SubmissionIcon from '../globals/Icons/SubmissionIcon.vue'

const submissions = [
  {
    creator: {
      name: 'Zachary Murray',
      handle: '@foreplayzach',
      avatar: 'https://publicassets.foreplay.co/zach_murray.png'
    },
    submitDate: '2025-08-26',
    socialPlatform: 'twitter',
    postUrl: 'https://twitter.com/foreplayzach/status/1699443569600454836'
  },
  {
    creator: {
      name: 'Zachary Murray',
      handle: '@foreplayzach',
      avatar: 'https://publicassets.foreplay.co/zach_murray.png'
    },
    submitDate: '2025-08-22',
    socialPlatform: 'linkedin',
    postUrl: 'https://www.linkedin.com/posts/zachary-murray_whats-easy-now-rarely-compounds-whats-activity-7338550273667133440-6lVu?utm_source=share&utm_medium=member_desktop&rcm=ACoAACr1wCEBXZafGmoQj8O025wL3Z4vuk96pz8'
  }
]

export default {
  name: 'BountyDrawer',
  components: {
    BountySubmissionCard,
    DueDateIcon,
    ShareIcon,
    BountyPayoutGradientIcon,
    DetailsIcon,
    SubmissionIcon
  },
  props: {
    bounty: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      activeTab: 'details',
      submissions
    }
  },
  computed: {
    formattedDueDate () {
      return moment(this.bounty.dueDate).format('MMMM DD, YYYY')
    }
  }
}
</script>

<style scoped>
.bounty-info-grid {
  display: grid;
  grid-template-columns: 1fr 48px 1fr;
}
.bounty-details {
  display: grid;
  grid-template-columns: 120px 1fr;
  column-gap: 12px;
  row-gap: 16px;
  align-items: center;
}
.tab-selector {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5.75px 8px;
  border-radius: 6px;
  color: #5C667A;
  transition: color 100ms ease-in-out, background-color 100ms ease-in-out;
}
.tab-selector:hover {
  color: #2F3547;
  background-color: #F6F8FA;
}
.tab-selector.active, .tab-selector.active:hover {
  color: #2F3547;
  background-color: transparent;
}
.tab-underline {
  position: absolute;
  z-index: 10;
  bottom: -8.5px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  height: 0px;
  border-bottom: 1px solid #06070F;
}

.tab-enter-active, .tab-leave-active {
  transition: width 150ms ease-in-out;
}
.tab-enter-from, .tab-enter, .tab-leave-to {
  width: 0%;
}
.tab-enter-to, .tab-leave-from {
  width: 100%;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@property --submit-btn-opacity-1 {
syntax: '<number>';
initial-value: 0.12;
inherits: false;
}
@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
.submit-btn {
  --submit-color: #121212;
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--submit-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--submit-btn-opacity-2)) 100%), var(--submit-color);
  color: white;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px var(--submit-color);
  transition: box-shadow 100ms ease-in-out,
    color 100ms ease-in-out, 
    background-color 100ms ease-in-out,
    --submit-btn-opacity-1 100ms ease-in-out, 
    --submit-btn-opacity-2 100ms ease-in-out,
    --submit-color 100ms ease-in-out;
}
.submit-btn:hover {
  --submit-btn-opacity-1: 0.24;
  --submit-btn-opacity-2: 0;
}

.desc-link {
  color: #1F69FF;
  text-decoration: underline;
  transition: color 100ms ease-in-out;
}
.desc-link:hover {
  color: #093AAE;
}
.decimal-list li {
  list-style-type: decimal;
}
</style>